/**
 * SignalR transport for 1:1 call signaling (R31 voice, R32 video).
 * Mirrors `features/games/api/dartsHub.ts` conventions: client class,
 * events interface, fake-able factory, auto-join of the per-call room
 * (`call:{callId}`) on CallStarted, leave on end, rejoin-on-reconnect.
 *
 * Wire contract (verbatim from `CallHub.cs`):
 * outgoing `InviteCall` / `InviteVideoCall` / `AcceptCall` / `RejectCall` /
 * `CancelCall` / `EndCall` / `JoinCall` / `LeaveCall`; incoming
 * `CallIncoming` / `CallInvited` / `CallStarted` / `CallRejected` /
 * `CallCancelled` / `CallEnded` / `CallError`. Video rides its own hub
 * method: SignalR matches by exact argument count, so a parameter on
 * `InviteCall` would break old clients.
 */
import * as signalR from '@microsoft/signalr'
import { hubAccessToken, hubUrl } from '@/lib/hubs'
import { api } from '@/lib/api'

export interface CallIncomingPayload {
  callId: string
  fromUserId: string
  pricePerMinute: number
  withVideo: boolean
}

export interface CallInvitedPayload {
  callId: string
  toUserId: string
  pricePerMinute: number
  withVideo: boolean
}

export interface CallStartedPayload {
  callId: string
}

export interface CallEndedPayload {
  callId: string
  reason?: string
  minutesBilled?: number
}

export interface CallErrorPayload {
  code: string
  message: string
}

export interface CallHubEvents {
  onIncoming?: (data: CallIncomingPayload) => void
  onInvited?: (data: CallInvitedPayload) => void
  onStarted?: (data: CallStartedPayload) => void
  onRejected?: (data: CallStartedPayload) => void
  onCancelled?: (data: CallStartedPayload) => void
  onEnded?: (data: CallEndedPayload) => void
  onError?: (error: unknown) => void
}

export type CallConnectionFactory = () => Pick<
  signalR.HubConnection,
  'on' | 'invoke' | 'start' | 'stop' | 'onclose' | 'onreconnecting' | 'onreconnected'
>

function defaultBuildConnection(): signalR.HubConnection {
  return new signalR.HubConnectionBuilder()
    .withUrl(hubUrl('call'), {
      accessTokenFactory: hubAccessToken,
      transport:
        signalR.HttpTransportType.WebSockets |
        signalR.HttpTransportType.ServerSentEvents,
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()
}

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

/** Normalizes one hub payload; null when `callId` is missing (row is dropped, like chat's parser). */
export function parseCallIdPayload(value: unknown): { callId: string } & Record<string, unknown> | null {
  const r = asRecord(value)
  const callId = r && str(r.callId)
  return r && callId ? { ...r, callId } : null
}

export interface CallToken {
  token: string
  roomName: string
  wsUrl: string
  expiresAt: string
}

/**
 * `POST /api/calls/{id}/token` → LiveKit publisher token for the
 * `call-{callId}` room. Accepts both camelCase and PascalCase keys
 * (FastEndpoints serializes the `CallTokenResponse` record either way
 * depending on the JSON options).
 */
export async function fetchCallToken(callId: string): Promise<CallToken> {
  const { data } = await api.post(`/calls/${encodeURIComponent(callId)}/token`)
  const r = asRecord(data) ?? {}
  const pick = (...keys: string[]): string | undefined => {
    for (const key of keys) {
      const v = str(r[key])
      if (v) return v
    }
    return undefined
  }
  const token = pick('token', 'Token')
  const roomName = pick('roomName', 'RoomName')
  const wsUrl = pick('wsUrl', 'WsUrl', 'wsURL')
  if (!token || !roomName || !wsUrl) throw new Error('Invalid call token response')
  return { token, roomName, wsUrl, expiresAt: pick('expiresAt', 'ExpiresAt') ?? '' }
}

export class CallHubClient {
  private connection: ReturnType<CallConnectionFactory> | null = null
  private connected = false
  /**
   * Per-call room (`call:{callId}`) the connection currently listens on.
   * In-call broadcasts (CallEnded) only reach group members, so the
   * client must join on CallStarted — nothing arrives otherwise. The
   * accepting side is added server-side by `AcceptCall`; the caller
   * joins explicitly through `JoinCall` here.
   */
  private currentRoom: string | null = null

  constructor(
    private events: CallHubEvents = {},
    private readonly build: CallConnectionFactory = defaultBuildConnection,
  ) {}

  get isConnected(): boolean {
    return this.connected
  }

  /** Mobile `setEvents` equivalent: the call hook owns reduction. */
  setEvents(events: CallHubEvents): void {
    this.events = events
  }

  async connect(): Promise<void> {
    if (this.connection && this.connected) return
    const connection = this.build()
    this.connection = connection
    this.wire(connection)
    try {
      await connection.start()
      this.connected = true
    } catch (err) {
      this.connected = false
      throw err
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.leaveRoom()
      await this.connection.stop()
      this.connected = false
      this.connection = null
    }
  }

  /** Best-effort join; a failure is surfaced via onError but never blocks the event. */
  private async joinRoom(callId: string): Promise<void> {
    if (!this.connection) return
    try {
      await this.connection.invoke('JoinCall', callId)
      this.currentRoom = callId
    } catch (err) {
      this.events.onError?.(err)
    }
  }

  /** Best-effort leave; never throws (teardown path). */
  private async leaveRoom(): Promise<void> {
    const room = this.currentRoom
    this.currentRoom = null
    if (!this.connection || !room) return
    try {
      await this.connection.invoke('LeaveCall', room)
    } catch {
      // Best-effort: the room dies with the call or the connection anyway.
    }
  }

  private assertConnected(): void {
    if (!this.connected || !this.connection) throw new Error('Not connected to call hub')
  }

  /**
   * Voice invite. Sends exactly one argument: SignalR requires an EXACT
   * argument-count match and throws for omitted optional hub params, so
   * the server takes no `withVideo` parameter at all (caught live).
   * Video uses {@link inviteVideo} (separate hub method) for the same reason.
   */
  async invite(toUserId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('InviteCall', toUserId)
  }

  /** Video invite: same single-argument shape against `InviteVideoCall`. */
  async inviteVideo(toUserId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('InviteVideoCall', toUserId)
  }

  async accept(callId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('AcceptCall', callId)
  }

  async reject(callId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('RejectCall', callId)
  }

  async cancel(callId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('CancelCall', callId)
  }

  async end(callId: string): Promise<void> {
    this.assertConnected()
    await this.connection!.invoke('EndCall', callId)
  }

  private wire(connection: ReturnType<CallConnectionFactory>): void {
    // Callbacks resolve through `this.events` on every invocation so a
    // later `setEvents` (call-hook wiring) takes effect on live connections.
    connection.on('CallIncoming', (...args: unknown[]) => {
      const r = asRecord(args[0])
      if (!r) return
      const callId = str(r.callId)
      const fromUserId = str(r.fromUserId)
      if (!callId || !fromUserId) return
      this.events.onIncoming?.({
        callId,
        fromUserId,
        pricePerMinute: num(r.pricePerMinute),
        withVideo: r.withVideo === true,
      })
    })
    connection.on('CallInvited', (...args: unknown[]) => {
      const r = asRecord(args[0])
      if (!r) return
      const callId = str(r.callId)
      const toUserId = str(r.toUserId)
      if (!callId || !toUserId) return
      this.events.onInvited?.({
        callId,
        toUserId,
        pricePerMinute: num(r.pricePerMinute),
        withVideo: r.withVideo === true,
      })
    })
    connection.on('CallStarted', (...args: unknown[]) => {
      const parsed = parseCallIdPayload(args[0])
      // Forward first so the UI reacts instantly; the room join runs
      // alongside (a failed join surfaces via onError, never blocks).
      if (parsed) this.events.onStarted?.({ callId: parsed.callId })
      if (parsed) void this.joinRoom(parsed.callId)
    })
    const endCall = (
      name: 'onRejected' | 'onCancelled' | 'onEnded',
      event: string,
    ) => {
      connection.on(event, (...args: unknown[]) => {
        // Forward first (same instant-UI reason as CallStarted), leave alongside.
        const parsed = parseCallIdPayload(args[0])
        if (parsed) {
          if (name === 'onEnded') {
            const r = asRecord(args[0])
            this.events.onEnded?.({
              callId: parsed.callId,
              reason: r ? str(r.reason) : undefined,
              minutesBilled: r && typeof r.minutesBilled === 'number' ? r.minutesBilled : undefined,
            })
          } else {
            this.events[name]?.({ callId: parsed.callId })
          }
        }
        void this.leaveRoom()
      })
    }
    endCall('onRejected', 'CallRejected')
    endCall('onCancelled', 'CallCancelled')
    endCall('onEnded', 'CallEnded')
    connection.on('CallError', (...args: unknown[]) => {
      const r = asRecord(args[0])
      const code = r && str(r.code)
      const message = r && str(r.message)
      this.events.onError?.(
        code || message ? { code: code ?? 'unknown', message: message ?? code ?? 'Call failed' } : args[0],
      )
    })
    connection.onclose(() => {
      this.connected = false
    })
    connection.onreconnecting(() => {
      this.connected = false
    })
    connection.onreconnected(() => {
      this.connected = true
      // Groups are per-connection server-side: a reconnected socket is not
      // in the room anymore until it rejoins.
      const room = this.currentRoom
      if (room && this.connection) {
        void this.connection
          .invoke('JoinCall', room)
          .catch((err: unknown) => this.events.onError?.(err))
      }
    })
  }
}

let hub: CallHubClient | null = null

/** App-scoped call hub (mobile injectable service). */
export function getCallHub(): CallHubClient {
  if (!hub) hub = new CallHubClient()
  return hub
}

/** Test-only: drop the singleton so the next caller builds a fresh one. */
export function resetCallHubForTests(): void {
  hub = null
}
